import type { PagedData } from '../types/PagedData.js';
import {
  applyBucketPatch,
  type BucketPatchArgs,
  type ChartClock,
  type ChartRow,
  readEventTsSeconds,
  readIntegerString,
  SYSTEM_CHART_CLOCK,
} from './live-bucket-core.js';

const AMM_TRADE_KIND = '1';

interface OutcomeChartLike {
  readonly candles?: ReadonlyArray<ChartRow>;
  readonly bucketSeconds?: number | null;
  readonly timeframe?: string;
}

export function applyAmmActivityToOutcomeChart<T extends OutcomeChartLike>(
  prev: T,
  raw: Record<string, unknown>,
  args: Record<string, unknown>,
  subjectMint?: string,
  clock: ChartClock = SYSTEM_CHART_CLOCK,
): T {
  if (
    typeof args.outcomeMint !== 'string' ||
    args.outcomeMint.length === 0 ||
    (subjectMint !== undefined && subjectMint !== args.outcomeMint)
  ) {
    return prev;
  }
  if (readIntegerString(raw, 'kind') !== AMM_TRADE_KIND) return prev;

  // Swap gate: current producers emit a positive execution price alongside
  // the post-reserve spot on every swap; LP/config activities carry post
  // state but create no trade candle fact, and a swap without a positive
  // execution price is not trusted to separate spot from execution. In both
  // cases leave the chart unchanged rather than patching a value absent from
  // the server candles. The chart sources the post-reserve spot
  // (`postOutcomePriceD9`), never the execution average.
  const executionPriceD9 = readIntegerString(raw, 'executionPriceD9');
  const spotD9 = readIntegerString(raw, 'postOutcomePriceD9');
  const eventTs = readEventTsSeconds(raw);
  if (
    executionPriceD9 === null ||
    BigInt(executionPriceD9) <= 0n ||
    spotD9 === null ||
    BigInt(spotD9) <= 0n ||
    eventTs === null ||
    !eventIsBeforeEnd(eventTs, args.end)
  ) {
    return prev;
  }
  const interval = effectiveInterval(prev, args);
  if (interval === '1mo') {
    return applyCalendarMonthPatch(prev, spotD9, eventTs, args, clock);
  }

  const snapshot: PagedData<ChartRow> = {
    data: prev.candles ?? [],
    nextCursor: null,
    hasMore: false,
    ...(typeof prev.bucketSeconds === 'number' ? { bucketSeconds: prev.bucketSeconds } : {}),
  };
  const next = applyBucketPatch(snapshot, chartArgs(args, interval), spotD9, eventTs, clock);
  return next === snapshot ? prev : ({ ...prev, candles: next.data } as T);
}

function applyCalendarMonthPatch<T extends OutcomeChartLike>(
  prev: T,
  priceD9: string,
  eventTs: number,
  args: Record<string, unknown>,
  clock: ChartClock,
): T {
  if (!eventIsAtOrAfterStart(eventTs, args.start)) return prev;
  const eventDate = new Date(eventTs * 1000);
  const nowDate = new Date(clock.nowSeconds() * 1000);
  if (!sameUtcMonth(eventDate, nowDate)) return prev;

  const candles = prev.candles ?? [];
  const monthBucket = monthStartIso(eventDate);
  if (candles.length === 0) {
    return { ...prev, candles: [newCandle(monthBucket, priceD9)] } as T;
  }

  const last = candles[candles.length - 1];
  if (last === undefined || !isOhlcv(last)) return prev;
  const lastDate = new Date(last.bucket);
  if (!Number.isFinite(lastDate.getTime())) return prev;
  const comparedMonth = compareUtcMonths(lastDate, eventDate);
  if (comparedMonth > 0) return prev;
  if (comparedMonth < 0) {
    return { ...prev, candles: [...candles, newCandle(monthBucket, priceD9)] } as T;
  }

  const high = BigInt(priceD9) > BigInt(last.high) ? priceD9 : last.high;
  const low = BigInt(priceD9) < BigInt(last.low) ? priceD9 : last.low;
  if (last.close === priceD9 && last.high === high && last.low === low) return prev;
  const nextCandles = candles.slice();
  nextCandles[nextCandles.length - 1] = { ...last, high, low, close: priceD9 };
  return { ...prev, candles: nextCandles } as T;
}

function chartArgs(
  args: Record<string, unknown>,
  interval: BucketPatchArgs['interval'],
): BucketPatchArgs {
  return {
    interval,
    chartType: 'ohlcv',
    ...(typeof args.start === 'string' ? { from: args.start } : {}),
  };
}

function effectiveInterval(
  prev: OutcomeChartLike,
  args: Record<string, unknown>,
): BucketPatchArgs['interval'] {
  return chartInterval(prev.timeframe ?? args.timeframe);
}

function chartInterval(value: unknown): BucketPatchArgs['interval'] {
  switch (value) {
    case '1m':
    case '5m':
    case '15m':
    case '1h':
    case '1d':
    case '1w':
    case '1mo':
      return value;
    default:
      return 'all';
  }
}

function eventIsAtOrAfterStart(eventTs: number, start: unknown): boolean {
  if (typeof start !== 'string') return true;
  const startMs = Date.parse(start);
  return Number.isFinite(startMs) && eventTs * 1000 >= startMs;
}

function eventIsBeforeEnd(eventTs: number, end: unknown): boolean {
  if (typeof end !== 'string') return true;
  const endMs = Date.parse(end);
  return Number.isFinite(endMs) && eventTs * 1000 < endMs;
}

function sameUtcMonth(left: Date, right: Date): boolean {
  return (
    left.getUTCFullYear() === right.getUTCFullYear() && left.getUTCMonth() === right.getUTCMonth()
  );
}

function compareUtcMonths(left: Date, right: Date): number {
  return (
    left.getUTCFullYear() * 12 +
    left.getUTCMonth() -
    (right.getUTCFullYear() * 12 + right.getUTCMonth())
  );
}

function monthStartIso(date: Date): string {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth())).toISOString();
}

function isOhlcv(row: ChartRow): row is Extract<ChartRow, { readonly open: string }> {
  return 'open' in row;
}

function newCandle(bucket: string, priceD9: string): Extract<ChartRow, { readonly open: string }> {
  return { bucket, open: priceD9, high: priceD9, low: priceD9, close: priceD9 };
}
